import { alpha } from '@mui/material/styles'
import type { Theme } from '@mui/material/styles'
import type { SystemStyleObject } from '@mui/system'

// Single source of truth for the Market Catalogue filter chip footprint. Both
// the taxonomy chips (MarketAnalyticsFilterBar) and the event category/type
// chips (CatalogueFilterBar) consume this so every chip in the filter region is
// exactly the same size. Height is set explicitly (rather than relying on MUI's
// 24px `size="small"` default) so chips that carry a color dot + count match
// plain text chips. 44px floor on coarse pointers keeps WCAG 2.5.5.
//
// Kept as a plain object (not `SxProps`) so callers can spread it into derived
// chip styles (e.g. the dashed "All" variant).
export const FILTER_CHIP_HEIGHT = 28

// Fixed width of the leading row label column ("GAME CATEGORY", "EVENT TYPE",
// etc.) shared by both the taxonomy card (MarketAnalyticsFilterBar) and the
// event card (CatalogueFilterBar). A fixed (not min-) width guarantees the first
// chip in every row starts at the same horizontal position, even when labels
// differ in length. Sized to fit the longest label ("GAME/EVENT CATEGORY").
export const FILTER_ROW_LABEL_WIDTH = 124

// Surface style for the filter-bar cards. Stays white in every state — a
// primary-tinted fill was tried to flag an active segment (review item 10) but
// sat too close to the primary-purple chips inside the card, so the border is
// the only cue now: neutral divider while nothing is selected, primary-tinted
// once a selection is active.
//
// Shared by `MarketAnalyticsFilterBar` (both the resolved bar and its skeleton)
// and `CatalogueFilterBar`'s event card so the two bars can't drift apart.
// Returned as a theme callback rather than a plain object because the tint needs
// `alpha()` over the live palette. Typed to the callback itself (not `SxProps`)
// so callers can still spread it into an `sx` array alongside their own styles.
export const filterBarSurfaceSx =
  (isActive: boolean) =>
  (theme: Theme): SystemStyleObject<Theme> => ({
    mb: 2,
    border: '1px solid',
    borderColor: isActive ? alpha(theme.palette.primary.main, 0.4) : theme.palette.divider,
    backgroundColor: theme.palette.background.paper,
  })

export const filterChipSx = {
  '&.MuiChip-root': {
    margin: 0,
    height: FILTER_CHIP_HEIGHT,
  },
  // The theme pins `labelSmall` to `3px 8px !important`, which looks cramped at
  // the taller 28px height (especially on short text chips). Restore balanced
  // horizontal padding; `!important` is required to beat the theme override.
  // Vertical padding stays 0 since the chip centers its label via flexbox.
  '& .MuiChip-label': {
    padding: '0 12px !important',
  },
  '@media (pointer: coarse)': {
    minHeight: 44,
  },
} as const

// Idle (unselected) treatment of an event-category-colored chip: colored outline and
// text over a faint wash of the same hue. Shared by the filter pills (`CountChip`) and
// the read-only event type chips on the Data Glossary cards, so an event type reads in
// its category's color wherever it appears.
export const categoryTintSx = (colorHex: string) => ({
  borderColor: colorHex,
  color: colorHex,
  backgroundColor: alpha(colorHex, 0.1),
})
